import {range} from '@benchling/js-utils';

import {assert, assertAndContinue} from 'Asserts';
import remergeStructuredTableSingleInputs from 'notebook/structured-tables/single-inputs/util/remergeStructuredTableSingleInputs';
import {
  StructuredTable,
  StructuredTableAdapter,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {getTableLengthCapacity} from 'notebook/structured-tables/util/checkTableCapacity';
import getDefaultBlankRow from 'notebook/structured-tables/util/getBlankRow';
import Excess from 'notebook/tables/excess';
import {
  Cell,
  ReplaceRowsTableOp,
  Row,
  RowMetadata,
  TableColumnType,
  TableOpDiff,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {areTypedValuesEquivalent} from 'notebook/tables/typed-values/DataTypes';
import notifications from 'notifications';

import {isTableLengthLocked} from './isTableLengthLocked';

interface AddRowsToTableOptions {
  filterReadonlyCells?: boolean; // Defaults to false, removes any cell values in readonly cells
  removeEmptyTrailingRows?: boolean; // Defaults to true
  addAtBeginning?: boolean; // Add the new rows to the beginning of the table
}

// TODO: move some of the helper functions into checkTableCapacity.ts
function countEmptyTrailingRows<TTableMetadata, TRowMetadata>(
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
): number {
  const lastNonEmptyIndex = getLastNonEmptyIndex(structuredTable);
  const lastIndex = structuredTable.rows.length - 1;
  return lastIndex - lastNonEmptyIndex;
}

function getLastNonEmptyIndex<TTableMetadata, TRowMetadata>(
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
): number {
  const {rows, columns} = structuredTable;
  const lastNonEmptyIndex = rows.findLastIndex((row, rowIdx) => {
    if (row.isFinalized) {
      return true;
    }
    // It is important to get a blank row for each row index value because that can affect the raw value of
    // relative spreadsheet formulas.
    const defaultRow = getDefaultBlankRow(rowIdx, columns, null);
    return !areRowValuesEquivalent(row.cells, defaultRow);
  });
  return lastNonEmptyIndex;
}

export function getMaxAllowedRowsToImport<TTableMetadata, TRowMetadata>(
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
): number {
  if (structuredTable?.metadata?.lockRowCount) {
    return countEmptyTrailingRows(structuredTable);
  } else {
    const lastNonEmptyIndex = getLastNonEmptyIndex(structuredTable);
    const nonEmptyRowCount = lastNonEmptyIndex + 1;

    const tableLengthCapacity = getTableLengthCapacity({
      structuredTableKind: structuredTable.kind,
      isTableEditable: true,
      numApiRowsPending: structuredTable.numApiRowsToAdd,
    });
    return tableLengthCapacity - nonEmptyRowCount;
  }
}

/**
 * Passes the given cell text through the tokenizer in order to format the formulas.
 * This will end up capitalizing the cell references.
 */
function tokenizeAndFormatCellValue(cellText: string): string {
  // Using {row: 0, column: 0} for both methods will ensure that the formulas are not changed, this will
  // only apply formatting to the cell references.
  const relativeText = Excess.tokenizer.toRelativeFormula(cellText, {row: 0, column: 0});
  const absoluteText = Excess.tokenizer.toAbsoluteFormula(relativeText, {row: 0, column: 0});
  return absoluteText;
}

/**
 * Returns true if the input rows are equivalent in the narrow sense that they have the same number of
 * columns and that the raw text and typed values of each cell are equivalent.
 */
export function areRowValuesEquivalent(row1: Array<Cell>, row2: Array<Cell>): boolean {
  if (row1.length !== row2.length) {
    return false;
  }

  for (const {cell1, cellIdx} of row1.map((cell1, cellIdx) => ({cell1, cellIdx}))) {
    const cell2 = row2[cellIdx];
    // Both cell texts need to be formatted before comparing them, because the tokenizer will capitalize the
    // cell references and this will have already been ran for defaultValues of the blankRow.
    if (
      tokenizeAndFormatCellValue(cell1.text) !== tokenizeAndFormatCellValue(cell2.text) ||
      !areTypedValuesEquivalent(cell1.typedValue ?? null, cell2.typedValue ?? null)
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Adds the specified rows to the table, merging in default values.
 *
 * All rows are appended. If the size of the new table would exceed ELN_MAX_STRUCTURED_TABLE_ROWS,
 * we return NULL.
 *
 * If the array of rowsToAdd is empty, then we also return NULL indicating there is no work to be done.
 */
export default function addRowsToTable<TTableMetadata, TRowMetadata>(
  rowsToAdd: Array<Row>,
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>,
  rowMetadatas: Array<TRowMetadata> | null,
  options: AddRowsToTableOptions = {},
  numPendingInAdded?: number // number of rows being added that are considered pending for the structured table
): {updatedTable: StructuredTable<TTableMetadata, TRowMetadata>; tableOp: TableOpDiff} | null {
  if (rowsToAdd.length === 0) {
    // Don't change the table if there are no rows to add
    return null;
  }
  assert(
    rowMetadatas == null || rowMetadatas.length === rowsToAdd.length,
    'Expected rowMetadatas to have the same length as rowsToAdd'
  );

  const numEmptyTrailingRows =
    (options.removeEmptyTrailingRows ?? true) ? countEmptyTrailingRows(structuredTable) : 0;

  // FYI: The following code does not account for mismatches between an imported spreadsheet
  // and the table to which the rows are being added. In the future, it would be better to
  // only install default values on columns that DO NOT exist in the imported spreadsheet.
  // So if a cell in column X of the spreadsheet is unspecified, do not use the default value.
  const startingRowIndex = options.addAtBeginning ? 0 : structuredTable.rows.length - numEmptyTrailingRows;
  const newRowsWithDefaultValues: Array<StructuredTableRow<TRowMetadata>> = rowsToAdd.map(
    (row, newRowIndex) => {
      const structuredTableRow = adapter.getBlankRow(
        structuredTable.metadata,
        startingRowIndex + newRowIndex,
        structuredTable.columns,
        rowMetadatas ? rowMetadatas[newRowIndex] : undefined
      );
      for (const [colIndex, cell] of row.entries()) {
        if (
          !TableUtil.isCellEmpty(cell) &&
          (!options.filterReadonlyCells ||
            !adapter.isPositionReadOnly(colIndex, structuredTableRow, structuredTable))
        ) {
          structuredTableRow.cells[colIndex] = cell;
        }
      }
      return structuredTableRow;
    }
  );

  const newTableRows = [
    ...(options.addAtBeginning ? newRowsWithDefaultValues : []),
    // Strip out empty trailing rows here
    ...structuredTable.rows.slice(0, structuredTable.rows.length - numEmptyTrailingRows),
    ...(options.addAtBeginning ? [] : newRowsWithDefaultValues),
  ];

  // Table edit op contains rows deleted from original table and rows inserted to produce the new table.
  // Additional inserts may occur below if the row length of the table is locked and we pad to maintain it.
  const tableOp: ReplaceRowsTableOp = {
    type: 'replace-rows',
    deleteIndexes: range(structuredTable.rows.length - numEmptyTrailingRows, structuredTable.rows.length - 1),
    insertIndexes: range(startingRowIndex, startingRowIndex + newRowsWithDefaultValues.length - 1),
  };

  // Pad with empty rows if table length is locked and more empty rows were removed than filled rows were added
  if (isTableLengthLocked(structuredTable) && newTableRows.length < structuredTable.rows.length) {
    const startingIdx = newTableRows.length;
    const paddedRowCount = structuredTable.rows.length - newTableRows.length;
    for (let paddedIdx = 0; paddedIdx < paddedRowCount; paddedIdx++) {
      tableOp.insertIndexes.push(newTableRows.length);
      newTableRows.push(
        adapter.getBlankRow(structuredTable.metadata, startingIdx + paddedIdx, structuredTable.columns)
      );
    }
  }

  const capacityErrorMessage = adapter.getTableLengthErrorMessage(
    structuredTable,
    newTableRows.length,
    true, // if you're calling addRowsToTable, we're assuming you're in an editable table
    numPendingInAdded
  );
  if (capacityErrorMessage != null) {
    notifications.error(capacityErrorMessage);
    return null;
  }

  // check that delegated row information is correct, if it isn't the entry won't save after the rows have been added
  const numDelegatedColumns = structuredTable.columns.filter(
    (col) => col.columnType === TableColumnType.ROW_DELEGATE
  ).length;
  if (numDelegatedColumns > 0) {
    if (
      !assertAndContinue(
        newTableRows.every((row) => {
          const delegateMap = (row.metadata as RowMetadata).delegatedColumns;
          return delegateMap && Object.keys(delegateMap).length === numDelegatedColumns;
        }),
        'One or more added rows do not have the correct number of entries in metadata.delegatedColumns.'
      )
    ) {
      notifications.error('There was an error with adding rows to this table.');
      return null;
    }
  }

  let updatedTable: StructuredTable<TTableMetadata, TRowMetadata> = {
    ...structuredTable,
    rows: newTableRows,
  };
  updatedTable = remergeStructuredTableSingleInputs(updatedTable);

  return {updatedTable, tableOp};
}
